import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Input,
  Button,
  Row,
  Col,
  Avatar,
  Typography,
  Select,
  message,
  Alert,
  Space,
  Tooltip,
  Tag,
  Collapse,
  Checkbox,
} from "antd";
import { useGlobal } from "../contexts/GlobalContext";
import request from "../utils/request";

const { TextArea } = Input;
const { Title, Paragraph, Text } = Typography;

interface PromptTemplate {
  id: string;
  avatar: string;
  name: string;
  description: string;
  content: string;
}

/** chat.html URL 参数 */
interface ChatUrlParams {
  userIcon?: string;
  userDesc?: string;
  userTitle?: string;
  showInviteLink?: boolean;
}

/** search.html URL 参数 */
interface SearchUrlParams {
  userIcon?: string;
  userTitle?: string;
  defaultSearchInput?: string;
}

const STORAGE_KEY = "ai-chat-url-params";

const promptTemplates: PromptTemplate[] = [
  {
    id: "1",
    avatar: "https://api.dicebear.com/7.x/miniavs/svg?seed=cat",
    name: "默认",
    description: "一个默认的智能聊天机器人",
    content:
      'Now, you are an intelligent chatbot integrated into the website, you need to answer relevant questions based on the information I provide, you need to reply in Chinese, taking into account the context: "{{answer}}" My question is: "{{question}}"',
  },
  {
    id: "2",
    avatar: "https://api.dicebear.com/7.x/miniavs/svg?seed=cat",
    name: "可爱猫娘",
    description: '一个说话都带"喵～"的可爱助手',
    content:
      '现在，你是一个集成到网站中的智能聊天机器人，你需要根据我提供的信息来回答相关的问题，每句话的结尾都要包含"喵～"，回答的相关信息可包含以下内容："{{answer}}"， 我的问题是："{{question}}"',
  },
  {
    id: "3",
    avatar: "https://api.dicebear.com/7.x/miniavs/svg?seed=zhenhuan",
    name: "甄嬛",
    description: "端庄优雅的甄嬛，擅长使用成语和古诗词",
    content:
      '现在，你扮演一个在甄嬛传中的角色甄嬛，而我是皇帝，你需要自称"臣妾"，叫我"皇上"，你需要根据我提供的信息来回答相关的问题，尽可能大量使用成语和古诗词，每句话的开头都要包含"皇上吉祥，"，回答的相关信息可包含以下内容："{{answer}}"， 我的问题是："{{question}}"',
  },
  {
    id: "4",
    avatar: "https://api.dicebear.com/7.x/miniavs/svg?seed=zhuli",
    name: "美女助理",
    description: "与老板关系暧昧的美女助理",
    content:
      '现在，你扮演我的美女助理，而我是你的老板，我们之间有暧昧关系，你表达的内容应该额外添加一暧昧成分，你需要根据我提供的信息来回答相关的问题，回答的相关信息可包含以下内容："{{answer}}"， 我的问题是："{{question}}"',
  },
  {
    id: "5",
    avatar: "https://api.dicebear.com/7.x/miniavs/svg?seed=laoban",
    name: "霸道总裁",
    description: "霸道但性格直爽的老板",
    content:
      '现在，你扮演我的老板，你比较霸道但是性格直爽，你会叫我"小可爱"。与你对话的人是你女助理，我们之间有暧昧关系，你表达的内容应该额外添加一些暧昧成分，你需要根据我提供的信息来回答相关的问题，但是如果提问内容过于简短或者态度不好时，你可以选择不回答并且欺负我，回答的相关信息可包含以下内容："{{answer}}"， 我的问题是："{{question}}"',
  },
  {
    id: "6",
    avatar: "https://api.dicebear.com/7.x/miniavs/svg?seed=professor",
    name: "专家模板",
    description: "专业的聊天模板",
    content:
      "<角色>\n角色：你是XX专家。\n</角色>\n<任务>\n任务：对于我提出的问题，你需要给出专业的回答。\n</任务>\n<对话>\n用户：{{question}}\n助手：{{answer}}\n</对话>\n<注意事项>\n注意事项：\n1. 你需要根据我提供的信息来回答相关的问题。\n2. 你需要用中文回答。\n3. 你需要尽量简洁明了。\n4. 你需要尽量使用专业术语。\n</注意事项>",
  },
];

const AIChat: React.FC = () => {
  const { user, projects, API_BASE_URL, token } = useGlobal();
  const [selectedProject, setSelectedProject] = useState<number | "not_using_knowledge_base">("not_using_knowledge_base");
  const [prompt, setPrompt] = useState<string>("");
  const [loading] = useState<boolean>(false);

  // URL 参数（持久化到 localStorage）
  const [chatParams, setChatParams] = useState<ChatUrlParams>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed.chat || {};
      }
    } catch {
      // ignore
    }
    return {};
  });
  const [searchParams, setSearchParams] = useState<SearchUrlParams>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed.search || {};
      }
    } catch {
      // ignore
    }
    return {};
  });

  const isParamsLoaded = useRef(false);
  const serverSyncReady = useRef(false);

  // U14:挂件参数以项目 config 为准,localStorage 仅作缓存。
  // 项目切换时从服务端拉取 widget_config 覆盖本地。
  useEffect(() => {
    if (selectedProject === "not_using_knowledge_base") return;
    serverSyncReady.current = false;
    request
      .get(`${API_BASE_URL}/api/v3/projects/${selectedProject}/widget-config`)
      .then((res) => {
        const cfg = res.data?.data || {};
        if (cfg.chat && typeof cfg.chat === "object") setChatParams(cfg.chat);
        if (cfg.search && typeof cfg.search === "object") setSearchParams(cfg.search);
      })
      .catch(() => {
        /* 拉取失败时沿用本地缓存 */
      })
      .finally(() => {
        serverSyncReady.current = true;
      });
  }, [API_BASE_URL, selectedProject]);

  // 保存参数:localStorage 即时缓存 + 服务端防抖持久化(跳过首次挂载与服务端回填)
  useEffect(() => {
    if (!isParamsLoaded.current) {
      isParamsLoaded.current = true;
      return;
    }
    const payload = { chat: chatParams, search: searchParams };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    if (selectedProject === "not_using_knowledge_base" || !serverSyncReady.current) return;
    const timer = window.setTimeout(() => {
      request
        .put(`${API_BASE_URL}/api/v3/projects/${selectedProject}/widget-config`, payload)
        .catch(() => {
          /* 静默失败,本地缓存仍在 */
        });
    }, 800);
    return () => window.clearTimeout(timer);
  }, [chatParams, searchParams, selectedProject, API_BASE_URL]);

  // U6:右侧 iframe 实时预览(300ms 防抖)
  const [previewTab, setPreviewTab] = useState<"chat" | "search">("chat");
  const [previewUrl, setPreviewUrl] = useState("");

  // 当 projects 加载完成后，设置默认选中的项目
  useEffect(() => {
    if (projects.length > 0 && selectedProject === "not_using_knowledge_base") {
      const postChatProjects = projects.filter(
        (project) => project.is_postchat
      );
      if (postChatProjects.length > 0) {
        setSelectedProject(postChatProjects[0].id);
        setPrompt(postChatProjects[0].prompt || "");
      }
    }
  }, [projects, selectedProject]);

  // 获取项目的 prompt
  useEffect(() => {
    if (selectedProject !== "not_using_knowledge_base") {
      const currentProject = projects.find((p) => p.id === selectedProject);
      if (currentProject?.prompt) {
        setPrompt(currentProject.prompt);
      }
    }
  }, [selectedProject, projects]);

  // 修改项目选项的生成方式
  const dialogOptions = [
    ...projects
      .filter((project) => project.is_postchat)
      .map((project) => ({
        value: project.id,
        label: project.name,
      })),
    { value: "not_using_knowledge_base", label: "不使用知识库" },
  ];

  const handlePromptSelect = (content: string) => {
    setPrompt(content);
  };

  const getChatUrl = () => {
    const baseUrl = "https://ai.zhheo.com/chat.html";
    const params = new URLSearchParams();
    const projectKey = projects.find((p) => p.id === selectedProject)?.key_value;
    if (projectKey) params.set("key", projectKey);
    if (chatParams.userIcon?.trim()) params.set("userIcon", chatParams.userIcon.trim());
    if (chatParams.userDesc?.trim()) params.set("userDesc", chatParams.userDesc.trim());
    if (chatParams.userTitle?.trim()) params.set("userTitle", chatParams.userTitle.trim());
    if (chatParams.showInviteLink) params.set("showInviteLink", "true");
    const query = params.toString();
    return query ? `${baseUrl}?${query}` : baseUrl;
  };

  const getSearchUrl = () => {
    const baseUrl = "https://ai.zhheo.com/search.html";
    const params = new URLSearchParams();
    const projectKey = projects.find((p) => p.id === selectedProject)?.key_value;
    if (projectKey) params.set("key", projectKey);
    if (searchParams.userIcon?.trim()) params.set("userIcon", searchParams.userIcon.trim());
    if (searchParams.userTitle?.trim()) params.set("userTitle", searchParams.userTitle.trim());
    if (searchParams.defaultSearchInput?.trim()) params.set("defaultSearchInput", searchParams.defaultSearchInput.trim());
    const query = params.toString();
    return query ? `${baseUrl}?${query}` : baseUrl;
  };

  // 预览 URL 300ms 防抖更新(U6)
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setPreviewUrl(previewTab === "chat" ? getChatUrl() : getSearchUrl());
    }, 300);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewTab, chatParams, searchParams, selectedProject, projects]);

  const handleChatClick = () => {
    window.open(getChatUrl(), "_blank");
  };

  const handleSearchClick = () => {
    window.open(getSearchUrl(), "_blank");
  };

  const handleCopyChatLink = async () => {
    try {
      await navigator.clipboard.writeText(getChatUrl());
      message.success("对话链接已复制到剪贴板");
    } catch {
      message.error("复制失败，请手动复制");
    }
  };

  const handleCopySearchLink = async () => {
    try {
      await navigator.clipboard.writeText(getSearchUrl());
      message.success("搜索链接已复制到剪贴板");
    } catch {
      message.error("复制失败，请手动复制");
    }
  };

  // 添加更新 prompt 的处理函数
  const handleUpdatePrompt = async () => {
    if (selectedProject === "not_using_knowledge_base") {
      message.error("请先选择项目");
      return;
    }

    const currentProject = projects.find((p) => p.id === selectedProject);
    if (!currentProject) {
      message.error("项目不存在");
      return;
    }

    try {
      const response = await request.put(
        `${API_BASE_URL}/api/v3/projects/${currentProject.id}/prompt`,
        {
          prompt: prompt,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.code === 200) {
        message.success("提示词更新成功");
      } else {
        message.error(response.data.msg || "提示词更新失败");
      }
    } catch (error) {
      message.error("提示词更新失败，请重试");
    }
  };

  // 跳转到MCP服务页面
  const handleGoToMCP = () => {
    window.location.href = "/console/mcp-server";
  };

  // 获取当前选中的项目信息
  const getCurrentProject = () => {
    if (selectedProject === "not_using_knowledge_base") return null;
    return projects.find((p) => p.id === selectedProject);
  };

  // 检查是否是 PostChat 会员
  if (!user?.apiInfo?.postchat.vip_info.is_vip) {
    return (
      <div
        style={{
          background: "#f0f2f5",
          minHeight: "100%",
          borderRadius: 8,
          padding: "16px",
        }}
      >
        <Card>
          <div style={{ textAlign: "center", padding: "32px" }}>
            <Title level={4}>需升级 Pro 及以上才可使用此功能</Title>
            <Button
              type="primary"
              onClick={() => (window.location.href = "/console")}
              style={{ marginTop: "16px" }}
            >
              返回首页升级
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const currentProject = getCurrentProject();

  return (
    <div
      style={{
        background: "#f0f2f5",
        minHeight: "100%",
        borderRadius: 8,
        padding: "16px",
      }}
    >
      <Card
        title={
          <Space>
            <i
              className="ri-tools-line"
              style={{ color: "#722ed1", fontSize: "16px" }}
            />
            <span style={{ color: "#722ed1" }}>MCP 工具服务</span>
          </Space>
        }
        variant="outlined"
        style={{
          marginBottom: "16px",
          background: "linear-gradient(135deg, #f6f4ff 0%, #f0ebff 100%)",
          border: "1px solid #e6d7ff",
        }}
        extra={
          <Button
            type="primary"
            size="small"
            onClick={handleGoToMCP}
            style={{
              background: "#722ed1",
              borderColor: "#722ed1",
            }}
          >
            <i className="ri-external-link-line" style={{ marginRight: 4 }} />
            配置工具
          </Button>
        }
      >
        <Alert
          message="增强您的AI助手能力"
          description={
            <div>
              <p style={{ margin: "8px 0" }}>
                通过 MCP (Model Context Protocol)
                工具服务，您的AI助手可以获得更强大的功能，暂仅支持 Pro Max
                及以上会员。
              </p>

              <p style={{ margin: "8px 0 0 0", color: "#666" }}>
                <i className="ri-information-line" style={{ marginRight: 4 }} />
                配置完成后，AI会根据对话需要自动调用相应工具，无需手动操作
              </p>
            </div>
          }
          type="info"
          showIcon
          icon={
            <i className="ri-lightbulb-line" style={{ color: "#722ed1" }} />
          }
        />
      </Card>

      {/* 项目选择卡片 */}
      <Card
        title={
          <span>
            <i
              className="ri-chat-3-line"
              style={{ marginRight: "8px", color: "#1677ff" }}
            ></i>
            项目选择
          </span>
        }
        variant="outlined"
        style={{ marginBottom: "16px" }}
      >
        <div
          style={{ display: "flex", gap: "8px", justifyContent: "flex-start", flexWrap: "wrap" }}
        >
          <Select
            value={selectedProject}
            onChange={setSelectedProject}
            options={dialogOptions}
            style={{ width: 200 }}
            placeholder="选择项目"
          />
          <Button type="primary" onClick={handleChatClick}>
            <i className="ri-chat-1-line"></i>
            对话
          </Button>
          {selectedProject !== "not_using_knowledge_base" && (
            <Button onClick={handleSearchClick}>
              <i className="ri-search-line"></i>
              搜索
            </Button>
          )}
        </div>

        {/* URL 参数设置（持久化到 localStorage） */}
        <Collapse
          style={{ marginTop: 16 }}
          items={[
            {
              key: "chat",
              label: (
                <span>
                  <i className="ri-chat-1-line" style={{ marginRight: 8 }} />
                  对话参数 (chat.html)
                </span>
              ),
              children: (
                <Space direction="vertical" style={{ width: "100%" }} size="middle">
                  <div>
                    <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
                      userIcon - 顶部图标/Logo 地址
                    </Text>
                    <Input
                      placeholder="https://example.com/your-logo.png"
                      value={chatParams.userIcon ?? ""}
                      onChange={(e) => setChatParams((p) => ({ ...p, userIcon: e.target.value }))}
                      allowClear
                    />
                  </div>
                  <div>
                    <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
                      userDesc - 顶部描述文案
                    </Text>
                    <Input
                      placeholder="未传时默认：如果你对网站的内容有任何疑问，可以来问我哦～"
                      value={chatParams.userDesc ?? ""}
                      onChange={(e) => setChatParams((p) => ({ ...p, userDesc: e.target.value }))}
                      allowClear
                    />
                  </div>
                  <div>
                    <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
                      userTitle - 标题文案（仅存在 key 时生效）
                    </Text>
                    <Input
                      placeholder="未传时默认：洪墨AI"
                      value={chatParams.userTitle ?? ""}
                      onChange={(e) => setChatParams((p) => ({ ...p, userTitle: e.target.value }))}
                      allowClear
                    />
                  </div>
                  <div>
                    <Checkbox
                      checked={chatParams.showInviteLink ?? false}
                      onChange={(e) => setChatParams((p) => ({ ...p, showInviteLink: e.target.checked }))}
                    >
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        showInviteLink - 为 true 时，点击 logo 或底部链接跳转邀请页
                      </Text>
                    </Checkbox>
                  </div>
                  <Button type="primary" onClick={handleCopyChatLink}>
                    <i className="ri-file-copy-line" style={{ marginRight: 4 }} />
                    复制对话链接
                  </Button>
                  <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
                    参数会自动保存到当前项目配置(本地仅作缓存),换设备登录同样生效
                  </Text>
                </Space>
              ),
            },
            {
              key: "search",
              label: (
                <span>
                  <i className="ri-search-line" style={{ marginRight: 8 }} />
                  搜索参数 (search.html)
                </span>
              ),
              children: (
                <Space direction="vertical" style={{ width: "100%" }} size="middle">
                  <div>
                    <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
                      userIcon - 顶部图标/Logo 地址
                    </Text>
                    <Input
                      placeholder="https://example.com/your-logo.png"
                      value={searchParams.userIcon ?? ""}
                      onChange={(e) => setSearchParams((p) => ({ ...p, userIcon: e.target.value }))}
                      allowClear
                    />
                  </div>
                  <div>
                    <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
                      userTitle - 顶部标题
                    </Text>
                    <Input
                      placeholder="未传时默认：PostChat"
                      value={searchParams.userTitle ?? ""}
                      onChange={(e) => setSearchParams((p) => ({ ...p, userTitle: e.target.value }))}
                      allowClear
                    />
                  </div>
                  <div>
                    <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
                      defaultSearchInput - 搜索框默认内容（仅预填，不自动搜索）
                    </Text>
                    <Input
                      placeholder="预填到搜索框的内容"
                      value={searchParams.defaultSearchInput ?? ""}
                      onChange={(e) => setSearchParams((p) => ({ ...p, defaultSearchInput: e.target.value }))}
                      allowClear
                    />
                  </div>
                  <Button type="primary" onClick={handleCopySearchLink}>
                    <i className="ri-file-copy-line" style={{ marginRight: 4 }} />
                    复制搜索链接
                  </Button>
                  <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
                    参数会自动保存到当前项目配置(本地仅作缓存),换设备登录同样生效
                  </Text>
                </Space>
              ),
            },
          ]}
        />

        {/* U6:挂件实时预览(300ms 防抖),保留新窗口/复制链接 */}
        {selectedProject !== "not_using_knowledge_base" && (
          <Card
            size="small"
            style={{ marginTop: 16 }}
            title={
              <Space>
                <i className="ri-eye-line" style={{ color: "#1677ff" }} />
                <span>实时预览</span>
                <Button.Group>
                  <Button size="small" type={previewTab === "chat" ? "primary" : "default"} onClick={() => setPreviewTab("chat")}>
                    对话
                  </Button>
                  <Button size="small" type={previewTab === "search" ? "primary" : "default"} onClick={() => setPreviewTab("search")}>
                    搜索
                  </Button>
                </Button.Group>
              </Space>
            }
            extra={
              <Space>
                <Button size="small" onClick={() => window.open(previewUrl, "_blank")}>
                  新窗口打开
                </Button>
                <Button
                  size="small"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(previewUrl);
                      message.success("链接已复制");
                    } catch {
                      message.error("复制失败,请手动复制");
                    }
                  }}
                >
                  复制链接
                </Button>
              </Space>
            }
          >
            {previewUrl && (
              <iframe
                key={previewUrl}
                src={previewUrl}
                title="挂件预览"
                style={{ width: "100%", height: 480, border: "1px solid #f0f0f0", borderRadius: 8 }}
              />
            )}
          </Card>
        )}

        {/* 项目MCP状态提示 */}
        {currentProject && (
          <div style={{ marginTop: "12px" }}>
            <Alert
              message={
                <Space>
                  <span>
                    {currentProject.enable_mcp
                      ? "✅ 当前项目已启用MCP工具服务"
                      : "⚠️ 当前项目未启用MCP工具服务"}
                  </span>
                  {!currentProject.enable_mcp && (
                    <Button
                      type="link"
                      size="small"
                      onClick={handleGoToMCP}
                      style={{ padding: 0, height: "auto" }}
                    >
                      立即启用
                    </Button>
                  )}
                </Space>
              }
              type={currentProject.enable_mcp ? "success" : "warning"}
              showIcon={false}
              style={{
                padding: "8px 12px",
                backgroundColor: currentProject.enable_mcp
                  ? "#f6ffed"
                  : "#fffbe6",
                border: `1px solid ${
                  currentProject.enable_mcp ? "#b7eb8f" : "#ffe58f"
                }`,
              }}
            />
          </div>
        )}
      </Card>

      {/* 根据选择的项目状态来控制显示 */}
      {selectedProject !== "not_using_knowledge_base" && (
        <>
          <Card
            title={
              <span>
                <i
                  className="ri-settings-3-line"
                  style={{ marginRight: "8px", color: "#1677ff" }}
                ></i>
                提示词设置
              </span>
            }
            variant="outlined"
            style={{ marginBottom: "16px" }}
          >
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Space size="small">
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    <i
                      className="ri-information-line"
                      style={{ marginRight: 4 }}
                    />
                    提示词用于定义AI的角色和回答方式，此处提示词主要用于前端AI客服能力。
                  </Text>
                </Space>
                <Space size="small">
                  <Tooltip title="点击插入问题变量,将被替换为用户的实际问题">
                    <Button
                      size="small"
                      type="text"
                      style={{
                        fontSize: 12,
                        padding: "0 8px",
                        height: 24,
                        border: "1px solid #d9d9d9",
                        borderRadius: 4,
                        color: "#722ed1",
                        fontFamily: "monospace",
                      }}
                      onClick={() => {
                        const textarea = document.querySelector(
                          'textarea[id="main-prompt"]'
                        ) as HTMLTextAreaElement;
                        if (textarea) {
                          const start = textarea.selectionStart;
                          const end = textarea.selectionEnd;
                          const newValue =
                            prompt.substring(0, start) +
                            "{{question}}" +
                            prompt.substring(end);
                          setPrompt(newValue);
                          setTimeout(() => {
                            textarea.focus();
                            textarea.setSelectionRange(start + 12, start + 12);
                          }, 0);
                        }
                      }}
                    >
                      <i
                        className="ri-question-line"
                        style={{ marginRight: 4 }}
                      />
                      {"{{question}}"}
                    </Button>
                  </Tooltip>
                  <Tooltip title="点击插入答案变量,将被替换为知识库检索到的相关信息">
                    <Button
                      size="small"
                      type="text"
                      style={{
                        fontSize: 12,
                        padding: "0 8px",
                        height: 24,
                        border: "1px solid #d9d9d9",
                        borderRadius: 4,
                        color: "#52c41a",
                        fontFamily: "monospace",
                      }}
                      onClick={() => {
                        const textarea = document.querySelector(
                          'textarea[id="main-prompt"]'
                        ) as HTMLTextAreaElement;
                        if (textarea) {
                          const start = textarea.selectionStart;
                          const end = textarea.selectionEnd;
                          const newValue =
                            prompt.substring(0, start) +
                            "{{answer}}" +
                            prompt.substring(end);
                          setPrompt(newValue);
                          setTimeout(() => {
                            textarea.focus();
                            textarea.setSelectionRange(start + 10, start + 10);
                          }, 0);
                        }
                      }}
                    >
                      <i
                        className="ri-database-2-line"
                        style={{ marginRight: 4 }}
                      />
                      {"{{answer}}"}
                    </Button>
                  </Tooltip>
                </Space>
              </div>

              <div style={{ position: "relative" }}>
                <TextArea
                  id="main-prompt"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder={`请输入提示词模板，例如:\n\n你是一个专业的客服机器人。\n\n用户问题: {{question}}\n知识库信息: {{answer}}\n\n请根据以上信息，用友好专业的语气回答用户的问题。`}
                  autoSize={{ minRows: 6, maxRows: 12 }}
                  maxLength={1000}
                  showCount
                  style={{
                    fontFamily: "inherit",
                    position: "relative",
                    zIndex: 1,
                  }}
                  className="prompt-textarea"
                />
              </div>

              <Alert
                message="变量说明"
                description={
                  <div style={{ fontSize: 13 }}>
                    <div style={{ marginBottom: 8 }}>
                      <Tag
                        color="purple"
                        style={{ fontFamily: "monospace", marginRight: 8 }}
                      >
                        {"{{question}}"}
                      </Tag>
                      <span>用户提出的问题,在对话时自动替换</span>
                    </div>
                    <div>
                      <Tag
                        color="green"
                        style={{ fontFamily: "monospace", marginRight: 8 }}
                      >
                        {"{{answer}}"}
                      </Tag>
                      <span>知识库检索到的相关信息,自动填充到提示词中</span>
                    </div>
                  </div>
                }
                type="info"
                showIcon
                icon={<i className="ri-lightbulb-line" />}
              />

              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  justifyContent: "flex-end",
                  alignItems: "center",
                }}
              >
                <Button
                  type="primary"
                  onClick={handleUpdatePrompt}
                  loading={loading}
                  icon={<i className="ri-save-line" />}
                >
                  保存提示词
                </Button>
              </div>
            </Space>
          </Card>

          <Card
            title={
              <span>
                <i
                  className="ri-store-2-line"
                  style={{ marginRight: "8px", color: "#1677ff" }}
                ></i>
                Prompt市场
              </span>
            }
            variant="outlined"
          >
            <Row gutter={[16, 16]}>
              {promptTemplates.map((template) => (
                <Col xs={24} sm={12} md={8} key={template.id}>
                  <Card
                    hoverable
                    onClick={() => handlePromptSelect(template.content)}
                    style={{ height: "100%" }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "start",
                        gap: "12px",
                      }}
                    >
                      <Avatar size={48} src={template.avatar} />
                      <div style={{ flex: 1 }}>
                        <Title level={5} style={{ margin: "0 0 4px 0" }}>
                          {template.name}
                        </Title>
                        <Paragraph style={{ margin: 0, color: "#666" }}>
                          {template.description}
                        </Paragraph>
                      </div>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
        </>
      )}
    </div>
  );
};

export default AIChat;
